import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Calendar, Target } from "lucide-react";

const forecastData = [
  {
    period: "Q1 2024",
    committed: 450000,
    bestCase: 620000,
    worstCase: 380000,
    accuracy: 92.5,
  },
  {
    period: "Q2 2024",
    committed: 520000,
    bestCase: 710000,
    worstCase: 420000,
    accuracy: 89.3,
  },
  {
    period: "Q3 2024",
    committed: 580000,
    bestCase: 780000,
    worstCase: 480000,
    accuracy: 91.2,
  },
];

const scenarios = [
  {
    name: "Conservative",
    probability: 85,
    revenue: 1850000,
    growth: 12.5,
    color: "bg-red-500",
  },
  {
    name: "Most Likely",
    probability: 60,
    revenue: 2100000,
    growth: 18.2,
    color: "bg-yellow-500",
  },
  {
    name: "Optimistic",
    probability: 35,
    revenue: 2450000,
    growth: 25.8,
    color: "bg-green-500",
  },
];

export const SalesForecasting: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("quarterly");

  return (
    <div className="space-y-6">
      <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
          <TabsTrigger value="annual">Annual</TabsTrigger>
        </TabsList>

        <TabsContent value="quarterly" className="space-y-6">
          {/* Forecast Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {forecastData.map((quarter) => (
              <Card key={quarter.period}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <span>{quarter.period}</span>
                    <Badge variant="outline" className="text-xs">
                      {quarter.accuracy}% Accuracy
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        ${quarter.committed.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Committed Forecast</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Best Case:</span>
                        <span className="font-medium">
                          ${quarter.bestCase.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Worst Case:</span>
                        <span className="font-medium">
                          ${quarter.worstCase.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Scenario Planning */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Scenario Planning
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {scenarios.map((scenario) => (
                  <div key={scenario.name} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium">{scenario.name}</h3>
                      <div className={`w-3 h-3 rounded-full ${scenario.color}`} />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xl font-bold">
                          ${scenario.revenue.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">Annual Revenue</p>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Probability:</span>
                        <span className="font-medium">{scenario.probability}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Growth:</span>
                        <span className="font-medium flex items-center gap-1">
                          {scenario.growth > 0 ? (
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-red-500" />
                          )}
                          {scenario.growth}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Forecast Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Forecast Visualization
              </CardTitle>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center text-gray-500">
              Interactive forecast chart will be implemented here
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monthly" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Forecast</CardTitle>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center text-gray-500">
              Monthly forecast view will be implemented here
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="annual" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Annual Forecast</CardTitle>
            </CardHeader>
            <CardContent className="h-64 flex items-center justify-center text-gray-500">
              Annual forecast view will be implemented here
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};